import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import { FiTrash2, FiPlus } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Cast icons as React.ElementType to fix TypeScript issues
const FiTrash2Icon = FiTrash2 as React.ElementType;
const FiPlusIcon = FiPlus as React.ElementType;

export interface HolidaySchedule {
  id: string;
  name: string;
  date: string; // YYYY-MM-DD format
  recurring: boolean;
  isActive: boolean;
}

export interface AppSettings {
  id: string;
  paymentStrategy?: any;
  businessHours?: any;
  bookingConstraints?: any;
  serviceAreaSettings?: any;
  bookingPoolSettings?: any;
  holidays?: HolidaySchedule[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const HolidayCalendarSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState<HolidaySchedule[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [holidayForm, setHolidayForm] = useState<HolidaySchedule>({
    id: '',
    name: '',
    date: '',
    recurring: false,
    isActive: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as AppSettings;
        if (data.holidays && Array.isArray(data.holidays)) {
          setHolidays(data.holidays);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load holiday calendar settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          holidays,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          paymentStrategy: 'collect_full_at_booking',
          holidays,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Holiday calendar updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save holiday calendar settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = () => {
    setHolidayForm({
      id: Date.now().toString(),
      name: '',
      date: '',
      recurring: false,
      isActive: true,
    });
    onOpen();
  };

  const handleSaveHoliday = () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const existingIndex = holidays.findIndex(h => h.id === holidayForm.id);
    let newHolidays: HolidaySchedule[];

    if (existingIndex >= 0) {
      newHolidays = [...holidays];
      newHolidays[existingIndex] = holidayForm;
    } else {
      newHolidays = [...holidays, holidayForm];
    }

    setHolidays(newHolidays);
    onClose();
    toast({
      title: 'Success',
      description: 'Holiday added',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDeleteHoliday = (id: string) => {
    const newHolidays = holidays.filter(h => h.id !== id);
    setHolidays(newHolidays);
    toast({
      title: 'Success',
      description: 'Holiday removed',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleToggleActive = (id: string) => {
    setHolidays(prev => prev.map(h => 
      h.id === id ? { ...h, isActive: !h.isActive } : h
    ));
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Holiday Calendar
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Manage holidays and special closure dates.
              No services will be available on these dates.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="md">Holidays</Heading>
                <Button
                  leftIcon={<FiPlusIcon />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleAddHoliday}
                >
                  Add Holiday
                </Button>
              </HStack>

              {holidays.length === 0 ? (
                <Alert status="info">
                  <AlertIcon />
                  No holidays configured. Click "Add Holiday" to create one.
                </Alert>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Date</Th>
                        <Th>Recurring</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {holidays.map((holiday) => (
                        <Tr key={holiday.id}>
                          <Td fontWeight="medium">{holiday.name}</Td>
                          <Td>{holiday.date}</Td>
                          <Td>{holiday.recurring ? 'Yes' : 'No'}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme={holiday.isActive ? 'green' : 'gray'}
                              onClick={() => handleToggleActive(holiday.id)}
                            >
                              {holiday.isActive ? 'Active' : 'Inactive'}
                            </Button>
                          </Td>
                          <Td>
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash2Icon />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          </VStack>
        </CardBody>
      </Card>

      <HStack spacing={3}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Holiday Calendar
        </Button>
      </HStack>

      {/* Add Holiday Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Holiday</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Holiday Name</FormLabel>
                <Input
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Christmas Day"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Date</FormLabel>
                <Input
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, date: e.target.value }))}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  For recurring holidays, use the first occurrence date
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} flex="1">
                  Recurring Yearly
                </FormLabel>
                <input
                  type="checkbox"
                  checked={holidayForm.recurring}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, recurring: e.target.checked }))}
                  style={{ width: '20px', height: '20px' }}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} flex="1">
                  Active
                </FormLabel>
                <input
                  type="checkbox"
                  checked={holidayForm.isActive}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  style={{ width: '20px', height: '20px' }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveHoliday}>
              Add Holiday
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HolidayCalendarSettings;

